import { Component } from '../../../../../lib/web/components/component';
import { ComponentBase } from '../../../../../lib/web/components/component-base';
import { MouseEnterListener } from '../../../../../lib/web/components/mouse-enter-listener';
import { MouseLeaveListener } from '../../../../../lib/web/components/mouse-leave-listener';
import { ChildRef } from '../../../../../lib/web/components/child-ref';
import { Dom } from '../../../../../lib/web/core/dom';
import { ClickListener } from '../../../../../lib/web/components/click-listener';
import { Input } from '../../../../../lib/web/components/input';
import { ApiService } from '../../../services/api.service';
import { EjsService } from '../../../services/ejs.service';
import { SessionService } from '../../../services/session.service';
import { CardNavigationService } from '../../../services/card-navigation.service';
import { UserWatchingHistoryService } from '../../../services/user-watching-history.service';
import { UserFavoriteService } from '../../../services/user-favorite.service';
import { MediaPlayerService, MediaType } from '../../../services/media-player.service';
import { Str } from '../../../../../lib/web/core/str';
import { SessionStorageService } from '../../../../../lib/web/services/session-storage.service';
import { DataEventService } from '../../../services/data-event.service';

const ITEMS_GAP: number = 12;
const EXPAND_DELAY_MS: number = 200;

@Component({
    selector: '.o-row'
})
export class RowComponent extends ComponentBase<HTMLElement> {

    @Input('apiUrl')
    private _apiUrl: string = null;

    private _data: any = null;

    private _expandTimeouts: Map<HTMLElement, number> = new Map();

    @ChildRef('.o-row__header')
    private _headerElement: HTMLDivElement = null;
    @ChildRef('.o-row__items')
    private _itemsElement: HTMLDivElement = null;
    @ChildRef('.o-row__item-arrow--start')
    private _startArrowElement: HTMLDivElement = null;
    @ChildRef('.o-row__item-arrow--end')
    private _endArrowElement: HTMLDivElement = null;

    public constructor(node: HTMLElement,
        private _sessionStorageService: SessionStorageService,
        private _sessionService: SessionService, 
        private _apiService: ApiService, 
        private _ejsService: EjsService, 
        private _cardNavigationService: CardNavigationService,
        private _mediaPlayerService: MediaPlayerService,    
        private _userWatchingHistoryService: UserWatchingHistoryService,
        private _userFavoriteService: UserFavoriteService,
        private _dataEventService: DataEventService) {

        super(node);

        this.addWindowEventListener('user-watching-history-changed', () => this.loadUserProgress());
        this.addWindowEventListener('user-favorites-changed', () => this.loadUserFavorites());
    }

    public onInit(mode?: 'load' | 'redirect'): void {
        if (this._apiUrl) {
            this.loadUserRow();
        }
        this._cardNavigationService.enableRowNavigation(this._node);

        this.addEventListener('mouseover', (e: MouseEvent) => this.onItemMouse(e), this._itemsElement);
        this.addEventListener('mouseout', (e: MouseEvent) => this.onItemMouse(e), this._itemsElement);        
        this.checkUserWatchingHistory();
        this.loadUserFavorites();
    }

    @ClickListener('.o-row__item')
    public onViewContent(event: MouseEvent): void {
        const item: HTMLAnchorElement = event.currentTarget as HTMLAnchorElement;        
        this._dataEventService.viewContent(new URL(item.href).pathname);
    } 

    @MouseEnterListener('.o-row__items-and-arrows')
    public onMouseEnter(): void {
        this.setOverflow();
    }

    @MouseLeaveListener('.o-row__items-and-arrows')
    public onMouseOut(): void {          
        this._endArrowElement.style.display = 'none';      
        this._startArrowElement.style.display = 'none';
    }

    @ClickListener('.o-row__item-arrow--start')
    public onStartArrowClick(): void {
        this.slide('start');
    }

    @ClickListener('.o-row__item-arrow--end')
    public onEndArrowClick(): void {
        this.slide('end');
    } 

    @ClickListener('.o-row__item-expandible-play')
    public onPlayClick(e: MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        this.playContent((e.target as HTMLElement).closest('.o-row__item') as HTMLAnchorElement);
    }

    @ClickListener('.o-row__item-expandible-share')
    public onShareClick(e: MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
    }

    @ClickListener('.o-row__item-expandible-favorite')
    public onFavoriteClick(e: MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        this.toggleFavorite((e.target as HTMLElement).closest('.o-row__item-expandible-favorite') as HTMLButtonElement);
    }

    private loadUserFavorites(): void {
        const buttons: HTMLButtonElement[] = Array.from(this._itemsElement.querySelectorAll('.o-row__item-expandible-favorite'));
        buttons.forEach(button => this.setFavoriteOption(button));
    }

    private setFavoriteOption(button: HTMLButtonElement): void {
        const isFavorite: boolean = this._userFavoriteService.isFavorite(parseInt(button.dataset.favoriteId), button.dataset.favoriteType as 'capsule' | 'collection' | 'partner-landing');
        const icon: HTMLElement = button.querySelector('.a-icon');
        const key: string = isFavorite ? 'remove': 'add';
        const previousKey: string = icon.dataset.key;
        icon.classList.replace(`a-icon--${previousKey}`, `a-icon--${key}`);
        icon.querySelector('i').classList.replace(`app-icons-${previousKey}`, `app-icons-${key}`);
        icon.dataset.key = key;
        button.classList.toggle('u-hidden-important', !this._sessionService.isSigned);
    }

    private async toggleFavorite(button: HTMLButtonElement): Promise<void> {
        const id: number = parseInt(button.dataset.favoriteId);
        const type: 'capsule' | 'collection' | 'partner-landing' = button.dataset.favoriteType as 'capsule' | 'collection' | 'partner-landing';
        const pathname: string = new URL((button.closest('.o-row__item') as HTMLAnchorElement).href).pathname;
        if (this._userFavoriteService.isFavorite(id, type)) {
            if (await this.showQuestion(USER_LOCALE.removeFromFavorite, USER_LOCALE.removeFromFavoriteQuestion.format('<br><br>'), 'ok', { result: 'cancel' }, { result: 'ok' }) == 'ok') {
                await this._userFavoriteService.remove(id, type);
                this._dataEventService.favorite(false, pathname);
            }
        }
        else {
            await this._userFavoriteService.add(id, type);
            this._dataEventService.favorite(true, pathname);
        }
    }

    private loadUserProgress(): void {
        const items: HTMLDivElement[] = Array.from(this._itemsElement.querySelectorAll('.o-row__item'));
        items?.forEach(item => {
            const id: number = parseInt(item.dataset.id);
            const watchingHistory: any = ['capsule', 'collection'].includes(this.getItemContentType(item)) ? this._userWatchingHistoryService.getItem(id, this.getItemContentType(item) as 'capsule' | 'collection') : null;
            this.setProgress(item, watchingHistory);
        });
    }

     public setProgress(itemElement: HTMLDivElement, data: { current: number, duration: number }): void {        
        if (itemElement) {
            const { current, duration } = data || {};
            if (current) {
                const progressValueElement: HTMLDivElement = itemElement.querySelector('.o-row__item-progress-value');
                if (progressValueElement) {
                    itemElement.classList.add('o-row__item--with-progress');
                    progressValueElement.style.width = `${((current || 0) / (duration || 1)) * 100}%`;
                }
            }
            else {
                itemElement.classList.remove('o-row__item--with-progress');
            }
        }
    }

    private setOverflow(): void {
        if (!document.body.classList.contains('touch')) {
            const items: HTMLLIElement[] = Array.from(this._itemsElement.querySelectorAll('.o-row__item'));
            const itemsWidth: number = items.sum(i => Dom.getComputedWidth(i)) + (items.length * ITEMS_GAP);
            if (itemsWidth > Dom.getComputedWidth(this._itemsElement) && (this._itemsElement.scrollLeft + this._itemsElement.offsetWidth + 1) < this._itemsElement.scrollWidth) {
                this._endArrowElement.style.display = 'flex';
            }
            else {
                this._endArrowElement.style.display = 'none';
            }
            if (this._itemsElement.scrollLeft) {
                this._startArrowElement.style.display = 'flex';
            }
            else {
                this._startArrowElement.style.display = 'none';
            }
        }
    }

    private slide(direction: 'start' | 'end'): void {        
        const itemsWidth: number = this._itemsElement.offsetWidth;
        const item: HTMLLIElement = this._itemsElement.querySelector('.o-row__item');
        const itemWidth: number = item.offsetWidth;
        const numberOfFullItems: number = Math.floor(itemsWidth / itemWidth);
        const distanceToScroll: number = numberOfFullItems * itemWidth;
        if (direction === 'end') {
            this._itemsElement.scrollLeft += distanceToScroll;
        } 
        else if (direction === 'start') {
            this._itemsElement.scrollLeft -= distanceToScroll;
        }
        setTimeout(() => this.setOverflow(), 500);
    }

    private async loadUserRow(): Promise<void> {
        if (this._sessionService.isSigned) {
            const response: any = await this._apiService.get(`/api/v1${this._apiUrl}`, null, { ignore: [0, 401] });
            // Evita renderizar si se ha navegado a otra página mientras se cargaba la fila
            if (!this.isDisposed && response?.items?.length) {
                this.loadData(response);
            }
        }
    }

    public loadData(data: any, embed: boolean = false): void {
        this._data = data;
        const { format, items } = data;
        this._node.classList.add(`o-row--${format}`);
        const headerHtml: string = this._ejsService.render('organisms/row/row-header', { data, TEXTS: USER_LOCALE });
        this._node.insertAdjacentHTML('afterbegin', headerHtml);
        const itemsHtml: string = (items || []).map((item: any, index: number) => this._ejsService.render('organisms/row/row-item', { data, item, TEXTS: USER_LOCALE, colindex: index + 1 })).join('');
        this._itemsElement.innerHTML = itemsHtml;

        if (embed) {
            Array.from(this._itemsElement.querySelectorAll('a')).forEach(a => {
                a.target = '_blank';
                a.classList.add('u-external-link');
            });
        }

        this.fragment();
        this.loadUserFavorites();
        this._node.classList.add('o-row--data-loaded');
                
        if (data.viewAllClick) {
            const viewAllElement: HTMLAnchorElement = this._node.querySelector('.o-row__title');
            if (viewAllElement) {
                viewAllElement.addEventListener('click', () => this._data.viewAllClick());
            }
        }
        if (data.itemClick) {
            const rowItems: HTMLAnchorElement[] = Array.from(this._node.querySelectorAll('.o-row__item'));
            rowItems.forEach((item, index) => {
                item.addEventListener('click', () => data.itemClick());
                item.setAttribute("aria-colindex", (index + 1).toString());
            });
        }

        // Re-enable navigation after new content is loaded
        this._cardNavigationService.enableRowNavigation(this._node);
        
        this.dispatchCustomEvent('row-data-loaded', { ...data });
    }

    public getItemContentType(itemElement: HTMLDivElement): string {
        if (['video', 'podcast'].includes(itemElement.dataset.type)) {
            return 'capsule';
        }
        else {
            return itemElement.dataset.type;
        }
    }

     private checkUserWatchingHistory(): void {
        const playButtons: HTMLButtonElement[] = Array.from(this._node.querySelectorAll('.o-row__item-expandible__play'));
        playButtons.forEach(button => {
            const item: HTMLDivElement = button.closest('.o-row__item') as HTMLDivElement;
            const contentId: number = parseInt(item.dataset.id);
            const contentType: string = item.dataset.type;
            const contentMediaType: string = item.dataset.mediaType;
            if (this._userWatchingHistoryService.isWatching(contentId, contentType === 'collection' ? 'collection': 'capsule')) {
                button.querySelector('span').innerText = contentMediaType === 'video' ? USER_LOCALE.continueWatching: USER_LOCALE.continueListening;
            }
        });
    }

    private async playContent(item: HTMLAnchorElement): Promise<void> {
        let contentUrl: string = item.getAttribute('href');
        const freePlay: boolean = item.dataset.freePlay === 'true';
        if (this._sessionService.isSigned || freePlay) {
            let capsuleId: number = ['video', 'podcast'].includes(item.dataset.type) ? parseInt(item.dataset.id): null;
            const collectionId: number = item.dataset.type === 'collection' ? parseInt(item.dataset.id): null;
            const image: string = item.querySelector('.o-row__item-image img').getAttribute('src');
            if (collectionId) {
                if (this._userWatchingHistoryService.isWatching(collectionId, 'collection')) {
                    capsuleId = this._userWatchingHistoryService.getItem(collectionId, 'collection').capsuleId;
                    const { isArchived } = this._userWatchingHistoryService.getItem(capsuleId, 'capsule');
                    contentUrl = await this._apiService.get(`/api/v1/collection/continue-url/${collectionId}/${capsuleId}/${isArchived}`);
                }
                else {
                    const { url, id } = await this._apiService.get(`/api/v1/collection/${collectionId}/first-capsule`);
                    contentUrl = url;
                    capsuleId = id;
                }
            }
            this.loadPlayer('capsule', {
                capsuleId,
                url: contentUrl,
                collectionId,
                image,
                playerType: item.dataset.playerType
            });
        }
        else {
            this._sessionStorageService.set('redirect-after-sign-in', contentUrl);
            this.redirect('/u/sign-in');
        }
    }

    private async loadPlayer(type: MediaType = 'capsule', content?: any): Promise<void> {                
        this._mediaPlayerService.loadMedia(content.playerType, {
            capsuleId: type == 'capsule' ? content.capsuleId: null,
            collectionId: content.collectionId,
            type,
            image: content.image,
            owner: type == 'capsule' 
                ? {  pathname: content.url,
                    target: content.url
                }
                : {
                    pathname: location.pathname,
                    target: Str.getRelativeUrl(location.href)
                }        
        });        
    }

    private onItemMouse(event: MouseEvent): void {
        const target: HTMLElement = event.target as HTMLElement;
        if (!target) {
            return;
        }
        const item: HTMLElement = target.closest('.o-row__item') as HTMLElement;
        if (!item || !this._itemsElement.contains(item)) {
            return;
        }
        const relatedTarget: Node = event.relatedTarget as Node;
        if (relatedTarget && item.contains(relatedTarget)) {
            return;
        }
        if (event.type === 'mouseover') {
            this.scheduleExpand(item);
        }
        else {
            this.collapseItem(item);
        }
    }

    private scheduleExpand(item: HTMLElement): void {
        if (this.isTouch || !item.classList.contains('o-row__item--expandible')) {
            return;
        }
        this.cancelExpand(item);
        const timeoutId: number = window.setTimeout(() => {
            this._expandTimeouts.delete(item);
            item.classList.add('o-row__item--expanded');
            this.scrollToEndIfLastExpandibleItem(item);
        }, EXPAND_DELAY_MS);
        this._expandTimeouts.set(item, timeoutId);
    }

    private cancelExpand(item: HTMLElement): void {
        const timeoutId: number = this._expandTimeouts.get(item);
        if (timeoutId) {
            window.clearTimeout(timeoutId);
            this._expandTimeouts.delete(item);
        }
    }

    private collapseItem(item: HTMLElement): void {
        this.cancelExpand(item);
        item.classList.remove('o-row__item--expanded');
    }

    private scrollToEndIfLastExpandibleItem(item: HTMLElement): void {
        if (this.isTouch || !item.classList.contains('o-row__item--expandible')) {
            return;
        }
        const items: HTMLElement[] = Array.from(this._itemsElement.querySelectorAll('.o-row__item')) as HTMLElement[];
        const lastItem: HTMLElement = items[items.length - 1];
        if (!lastItem || lastItem !== item) {
            return;
        }

        setTimeout(() => {
            const maxScrollLeft: number = Math.max(0, this._itemsElement.scrollWidth - this._itemsElement.clientWidth);
            if (this._itemsElement.scrollLeft !== maxScrollLeft) {
                this._itemsElement.scrollTo({ left: maxScrollLeft, behavior: 'smooth' });
            }
        }, 200);
    }

    public dispose(): void {
        this._expandTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
        this._expandTimeouts.clear();
        this._cardNavigationService.disableNavigation(this._node);
        super.dispose();
    }
}